/**
 * EntityRefBadge — small wrapper for `<EntityBadge>` used by `buildJsonSubItems`
 * to render entity-ref fields inside JSON sub-items. Lives in its own file so
 * the helper module (`buildJsonSubItems.tsx`) can stay HMR-friendly per the
 * `react-refresh/only-export-components` rule.
 */

import { useCallback, useMemo } from 'react';

import { useOrg } from '@/contexts/OrgContext';
import { EntityBadgeById } from '@/lib/crm/components/badges';
import { EntityBadge } from '@/lib/crm/components/badges/EntityBadge';
import type { EntityBadgeState } from '@/lib/crm/components/badges/types';

import type { HydratedEntity } from '../types';

interface EntityRefBadgeProps {
  entityId: string;
  obj: HydratedEntity;
  onEntityClick: ((entityId: string) => void) | undefined;
}

export function EntityRefBadge({
  entityId,
  obj,
  onEntityClick,
}: EntityRefBadgeProps) {
  const state = useMemo<EntityBadgeState>(
    () => ({
      kind: 'data',
      data: {
        id: obj.entity_id,
        entityType: obj.entity_type_name,
        displayName: obj.ahoy_display_name ?? 'Unknown',
      },
    }),
    [obj]
  );
  const handleClick = useCallback(() => {
    onEntityClick?.(entityId);
  }, [onEntityClick, entityId]);
  return (
    <EntityBadge
      state={state}
      variant='xs'
      className='inline-flex align-middle'
      onClick={onEntityClick ? handleClick : undefined}
    />
  );
}

/**
 * Fallback for entity refs missing from the timeline's `lookups.entities`
 * pool: resolves the display name through the CRM store (fetching on
 * demand) via EntityBadgeById. Never renders a raw or truncated entity id.
 */
export function HydratedEntityRef({ entityId }: { entityId: string }) {
  const { currentOrg } = useOrg();
  if (!currentOrg?.id) {
    return <span className='font-medium text-fg-tertiary'>Unknown</span>;
  }
  return (
    <EntityBadgeById
      entityId={entityId}
      orgId={currentOrg.id}
      variant='xs'
      className='inline-flex align-middle'
    />
  );
}
